import axios from 'axios';

import { API } from '@/config/api';
import { env } from '@/config/env';

import { tokenStorage } from './tokenStorage';

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
}

/**
 * The refresh currently in flight, if there is one.
 *
 * <h2>Single-flight, and this is not an optimisation</h2>
 *
 * The backend rotates refresh tokens — the registered client sets
 * `reuse-refresh-tokens: false` — and keeps a table of superseded ones so that
 * presenting a spent refresh token is detected as reuse. That is the standard
 * defence against a stolen token, and it means the whole chain can be revoked
 * when it fires.
 *
 * <p>So two requests 401-ing at the same moment must not both refresh. The second
 * would present a token the first had already spent, which is indistinguishable
 * from theft — and the correct response to theft is to invalidate everything,
 * signing the person out for the crime of having a dashboard that loads two
 * things at once.
 *
 * <p>Everyone therefore awaits the same promise.
 */
let inFlight: Promise<string> | null = null;

/**
 * Exchanges the refresh token for a new access token.
 *
 * <p>Plain `axios`, not the shared client, and deliberately: this call must not
 * pass through the interceptor that reacts to a 401 by refreshing. A failed
 * refresh would trigger a refresh, forever.
 */
async function exchange(): Promise<string> {
  const refreshToken = tokenStorage.refreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: env.oauthClientId,
    refresh_token: refreshToken,
  });

  const { data } = await axios.post<TokenResponse>(`${env.apiBaseUrl}${API.token}`, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  // Both, not just the access token. Rotation means the refresh token that came
  // back replaces the one just spent; keeping the old one guarantees the next
  // refresh presents a superseded token and is treated as reuse.
  tokenStorage.save({ accessToken: data.access_token, refreshToken: data.refresh_token });

  return data.access_token;
}

/**
 * A fresh access token, refreshing at most once no matter how many callers ask.
 *
 * @throws when there is no refresh token, or the server refuses it — the caller
 *         is then out of options and the session is over
 */
export function refreshAccessToken(): Promise<string> {
  inFlight ??= exchange().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Forgets the in-flight refresh. For tests, which must not share one. */
export function resetRefreshState(): void {
  inFlight = null;
}
